import { useQuery } from '@tanstack/react-query';
import { authenticatedFetch, isAuthenticated } from '@/lib/api';

interface SettingItem {
  id: number;
  Name: string;
  Value: string;
}

interface SettingsResponse {
  Header: any[];
  EditableColumns: any[];
  ValueChangeColumns: any[];
  TwoFields: any[];
  status: number;
  message: string;
  data: SettingItem[];
  TotalRecords: number;
}

export const useSettings = () => {
  const { data: settingsResponse, isLoading, error } = useQuery<SettingsResponse>({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await authenticatedFetch<SettingsResponse>('/api/Settings?Count=100');
      return response.data;
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    enabled: isAuthenticated(), // Only fetch when user is authenticated
    retry: false, // Don't retry on 401 errors
  });

  const settings = settingsResponse?.data || [];

  // Primary Company ID from Company & Misc - used as default for company dropdowns app-wide
  const primaryCompanyId = (settings.find(s => s.Name === 'Primary Company ID')?.Value || '').trim();

  // Helper function to get setting value by name
  const getSetting = (name: string): string => {
    const setting = settings.find(s => s.Name === name);
    return setting?.Value || '';
  };

  // Helper function to get boolean setting (0/1 to boolean)
  const getBooleanSetting = (name: string): boolean => {
    const value = getSetting(name);
    return value === '1';
  };

  // Specific settings for SalesInvoice
  const salesInvoiceSettings = {
    // Invoice settings
    invoicePrintTemplateV2: getSetting('Invoice Print Template V2') || '0', // 0=2inch, 1=3inch, 2=A4
    invoiceQtyPopupRequired: getBooleanSetting('Invoice Qty Popup Required'),
    invoicePartnerRequired: getBooleanSetting('Invoice Partner Required'),
    imageInInvoice: getBooleanSetting('Image in Invoice'),
    fifoInvoiceOrder: getBooleanSetting('FIFO Invoice Order'),
    duplicateInvoiceLineItems: getBooleanSetting('Duplicate Invoice Line Items'),
    splitPayment: getBooleanSetting('Split Payment'),
    shipAddressNeeded: getBooleanSetting('Ship address needed'),
    showCostPrice: getBooleanSetting('isCostPrice'),
    unknownUserTicketed: getBooleanSetting('Unknown User Ticketed'),

    // Inventory settings
    imgInInventory: getBooleanSetting('Img in Inventory'),
    
    // Field labels
    tag1Name: getSetting('InvTag1'),
    tag2Name: getSetting('InvTag2'),
  };

  return {
    settings,
    isLoading,
    error,
    getSetting,
    getBooleanSetting,
    salesInvoiceSettings,
    primaryCompanyId,
  };
};
